import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ConfirmDialog from './ConfirmDialog';
import { useHorizontalScroll } from './useSideScroll';
import './ExistingScreenshots.css';

/**
 * The screenshots already published for an app, with a way to unpublish one.
 *
 * Replaces ImageListUpdate. The delete behaviour is carried over unchanged —
 * POST the encoded path, refetch the app list, drop the entry from the session
 * copy — but the bare-"Yes" Popover it used is now a ConfirmDialog with a
 * Cancel, and MUI's woven ImageList is a plain token-styled strip.
 *
 * Deletion is immediate and server-side: it is not staged until the form is
 * saved, which is why it asks first.
 */
export default function ExistingScreenshots({ screenshotList, app, setComps }) {
  const scrollRef = useHorizontalScroll();
  const [pending, setPending] = useState(null);

  if (!screenshotList || screenshotList.length === 0) return null;

  async function handleDelete(encoded) {
    setPending(null);

    const APILinksFetch = await fetch('/APILinks.json');
    const APILinks = await APILinksFetch.json();
    const response = await fetch(APILinks['deleteScreenshot'] + `screenshot=${encoded}`, {
      method: 'POST',
    });

    const deleteData = await response.json();

    if (deleteData['message'] === 'Deleted') {
      const res = await fetch(APILinks['getAppList']);
      const data = await res.json();

      const index = screenshotList.indexOf(decodeURIComponent(encoded));
      if (index !== -1) screenshotList.splice(index, 1);

      app.screenshots = screenshotList;
      sessionStorage.setItem('app', JSON.stringify(app));
      setComps(data);
    }
  }

  return (
    <div className="itcShots">
      <ul className="itcShotsStrip" ref={scrollRef}>
        {screenshotList.map((item) => {
          const encoded = encodeURIComponent(item);
          return (
            <li className="itcShotsItem" key={encoded}>
              <img className="itcShotsImage" src={encoded} alt="Published screenshot" />
              <button
                type="button"
                className="itcShotsRemove"
                aria-label="Remove screenshot"
                onClick={() => setPending(encoded)}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </li>
          );
        })}
      </ul>

      <ConfirmDialog
        open={pending !== null}
        title="Remove this screenshot?"
        description="It comes off the live listing straight away, before you save the rest of the form. Uploading it again is the only way back."
        confirmLabel="Remove"
        onConfirm={() => handleDelete(pending)}
        onClose={() => setPending(null)}
      />
    </div>
  );
}

ExistingScreenshots.propTypes = {
  screenshotList: PropTypes.array,
  app: PropTypes.object,
  setComps: PropTypes.func,
};
